import type { Plugin } from "../core/plugin";
import { PluginRegistry } from "../core/pluginRegistry";

/** Duración de la animación cortina del modal (debe coincidir con el CSS en tablet) */
const MODAL_CURTAIN_DURATION_MS = 500;

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "area[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "iframe",
  "object",
  "embed",
  "[tabindex]:not([tabindex='-1'])",
  "[contenteditable='true']",
].join(",");

export class ModalManager implements Plugin {
  private readonly content: HTMLElement | null;
  private readonly closeButtons: NodeListOf<HTMLElement>;
  private isOpen: boolean = false;
  private closeTimeout: ReturnType<typeof setTimeout> | null = null;
  private openerElement: HTMLElement | null = null;

  private hasOtherOpenModalOverlays(): boolean {
    const overlays = document.querySelectorAll<HTMLElement>(
      ".com-modalB2C__overlay, .com-modalB2B__overlay",
    );

    return Array.from(overlays).some((overlay) => {
      if (overlay === this.element) return false;
      if (!overlay.classList.contains("is-open")) return false;
      return window.getComputedStyle(overlay).display !== "none";
    });
  }

  constructor(private readonly element: HTMLElement) {
    this.content = element.querySelector('[data-name="com-modalB2B-content"]');
    this.closeButtons = element.querySelectorAll(
      ".modal-close-btn, [data-modal-close]",
    );
  }

  init(): void {
    this.close();
    this.element.addEventListener("modal:open", () => this.open());
    this.element.addEventListener("modal:close", () => this.close());

    this.element.addEventListener("click", (e) => {
      const target = e.target as HTMLElement;
      if (this.isOpen && target === this.element) {
        this.close();
      }
    });

    this.closeButtons.forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        this.close();
      });
    });

    document.addEventListener("keydown", (e) => {
      if (!this.isOpen) return;
      if (this.element.style.display === "none") return;
      if (e.key === "Escape") {
        this.close();
        return;
      }
      if (e.key === "Tab") {
        this.handleFocusTrap(e);
      }
    });
  }

  private getFocusableElements(): HTMLElement[] {
    const root = this.content ?? this.element;
    return Array.from(
      root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
    ).filter((el) => !el.hasAttribute("disabled") && el.offsetParent !== null);
  }

  private focusFirst(): void {
    const focusable = this.getFocusableElements();
    const target = focusable[0] ?? this.content ?? this.element;
    if (target === this.content || target === this.element) {
      if (!target.hasAttribute("tabindex")) {
        target.setAttribute("tabindex", "-1");
      }
    }
    target.focus();
  }

  private handleFocusTrap(e: KeyboardEvent): void {
    const focusable = this.getFocusableElements();
    if (focusable.length === 0) {
      e.preventDefault();
      (this.content ?? this.element).focus();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement as HTMLElement | null;
    if (
      e.shiftKey &&
      (active === first || !active || !this.element.contains(active))
    ) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  public open(): void {
    if (this.closeTimeout !== null) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }
    this.openerElement = document.activeElement as HTMLElement | null;
    this.isOpen = true;
    this.element.style.display = "flex";
    document.body.style.overflow = "hidden";
    // Retrasar is-open un frame para que se pinte primero left: -360px y la transición se vea suave (efecto cortina)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.element.classList.add("is-open");
        this.focusFirst();
      });
    });
    this.element.dispatchEvent(
      new CustomEvent("modal:opened", { bubbles: true }),
    );
  }

  public close(): void {
    this.isOpen = false;
    this.element.classList.remove("is-open");
    const opener = this.openerElement;
    this.openerElement = null;
    this.element.dispatchEvent(
      new CustomEvent("modal:closed", { bubbles: true }),
    );
    // Esperar a que termine la animación de cierre (cortina hacia la izquierda) antes de ocultar el overlay
    this.closeTimeout = setTimeout(() => {
      this.element.style.display = "none";
      document.body.style.overflow = this.hasOtherOpenModalOverlays()
        ? "hidden"
        : "";
      this.closeTimeout = null;
      // Devolver el foco al elemento que abrió la modal una vez oculta la cortina.
      if (
        opener &&
        typeof opener.focus === "function" &&
        document.contains(opener)
      ) {
        opener.focus();
      }
    }, MODAL_CURTAIN_DURATION_MS);
  }
}

PluginRegistry.register("modal-manager", ModalManager);
